import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './UserManagement.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function UserManagement({ currentUser }) {
    const [users, setUsers] = useState([]);
    const [editingUser, setEditingUser] = useState(null);
    const [newEmail, setNewEmail] = useState('');
    const [newUsername, setNewUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newIsSuperuser, setNewIsSuperuser] = useState(false);
    const [newIsGlobalAdmin, setNewIsGlobalAdmin] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API_BASE}/users`, {
                withCredentials: true
            });
            setUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
            if (error.response?.status === 403) {
                setError('Superuser access required');
            } else {
                setError('Failed to load users');
            }
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (!newEmail.trim()) {
            setError('Email is required');
            return;
        }
        if (!newUsername.trim()) {
            setError('Username is required');
            return;
        }
        if (!newPassword || newPassword.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await axios.post(`${API_BASE}/users`, {
                email: newEmail,
                username: newUsername,
                password: newPassword,
                isSuperuser: newIsSuperuser,
                isGlobalAdmin: newIsGlobalAdmin
            }, {
                withCredentials: true
            });

            setNewEmail('');
            setNewUsername('');
            setNewPassword('');
            setNewIsSuperuser(false);
            setNewIsGlobalAdmin(false);
            fetchUsers();
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to create user');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateUser = async (userId, email, username, password, isSuperuser, isGlobalAdmin) => {
        setLoading(true);
        setError('');

        try {
            await axios.put(`${API_BASE}/users/${userId}`, {
                email,
                username,
                password: password || undefined,
                isSuperuser,
                isGlobalAdmin
            }, {
                withCredentials: true
            });

            setEditingUser(null);
            fetchUsers();
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to update user');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user? Their photos will remain.')) {
            return;
        }

        setLoading(true);
        setError('');

        try {
            await axios.delete(`${API_BASE}/users/${userId}`, {
                withCredentials: true
            });

            fetchUsers();
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to delete user');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="user-management-container">
            {error && <div className="error-message">{error}</div>}

            <div className="create-user-section">
                <h3>Create New User</h3>
                <form onSubmit={handleCreateUser}>
                    <input
                        type="email"
                        placeholder="Email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        disabled={loading}
                    />
                    <input
                        type="text"
                        placeholder="Username"
                        value={newUsername}
                        onChange={(e) => setNewUsername(e.target.value)}
                        disabled={loading}
                    />
                    <input
                        type="password"
                        placeholder="Password (min 6 characters)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={loading}
                    />
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={newIsSuperuser}
                            onChange={(e) => setNewIsSuperuser(e.target.checked)}
                            disabled={loading}
                        />
                        <span>Superuser</span>
                    </label>
                    {currentUser?.isGlobalAdmin && (
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={newIsGlobalAdmin}
                                onChange={(e) => setNewIsGlobalAdmin(e.target.checked)}
                                disabled={loading}
                            />
                            <span>Global Admin</span>
                        </label>
                    )}
                    <button type="submit" disabled={loading}>
                        {loading ? 'Creating...' : 'Create User'}
                    </button>
                </form>
            </div>

            <div className="users-list-section">
                <h3>Users</h3>
                <div className="users-list">
                    {users.map(user => (
                        <div key={user.id} className="user-item">
                            {editingUser?.id === user.id ? (
                                <div className="user-edit-form">
                                    <input
                                        type="email"
                                        placeholder="Email"
                                        value={editingUser.email}
                                        onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Username"
                                        value={editingUser.username}
                                        onChange={(e) => setEditingUser({...editingUser, username: e.target.value})}
                                    />
                                    <input
                                        type="password"
                                        placeholder="New password (leave empty to keep current)"
                                        value={editingUser.password || ''}
                                        onChange={(e) => setEditingUser({...editingUser, password: e.target.value})}
                                    />
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={editingUser.is_superuser || false}
                                            onChange={(e) => setEditingUser({...editingUser, is_superuser: e.target.checked})}
                                        />
                                        <span>Superuser</span>
                                    </label>
                                    {currentUser?.isGlobalAdmin && (
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={editingUser.is_global_admin || false}
                                                onChange={(e) => setEditingUser({...editingUser, is_global_admin: e.target.checked})}
                                            />
                                            <span>Global Admin</span>
                                        </label>
                                    )}
                                    <div className="user-edit-actions">
                                        <button onClick={() => handleUpdateUser(
                                            user.id,
                                            editingUser.email,
                                            editingUser.username,
                                            editingUser.password,
                                            editingUser.is_superuser,
                                            editingUser.is_global_admin
                                        )}>
                                            Save
                                        </button>
                                        <button onClick={() => setEditingUser(null)}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="user-info">
                                        <h4>
                                            {user.username}
                                            {user.is_superuser && <span className="superuser-badge">Superuser</span>}
                                            {user.is_global_admin && <span className="global-admin-badge">Global Admin</span>}
                                        </h4>
                                        <p className="user-email">{user.email}</p>
                                        <p className="user-date">Created: {new Date(user.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <div className="user-actions">
                                        <button onClick={() => setEditingUser({...user, password: ''})}>Edit</button>
                                        <button onClick={() => handleDeleteUser(user.id)} className="delete-btn">
                                            Delete
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default UserManagement;
